/**
 * Parse Better Auth Errors
 * Utility to extract error codes and messages from Better Auth error objects
 */

import { getErrorMessage, inferErrorCodeFromMessage, type ErrorMessageConfig } from "./error-messages";

function readErrorCode(error: unknown): string | null {
  if (error instanceof Error) {
    const code = (error as Error & { code?: string }).code;
    return code ?? null;
  }
  if (typeof error === "object" && error !== null) {
    const record = error as Record<string, unknown>;
    const nested = record.error;
    if (typeof nested === "object" && nested !== null) {
      const nestedCode = (nested as Record<string, unknown>).code;
      if (typeof nestedCode === "string") return nestedCode;
    }
    if (typeof record.code === "string") return record.code;
  }
  return null;
}

function readErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message || "Erro desconhecido";
  }
  if (typeof error === "object" && error !== null) {
    const record = error as Record<string, unknown>;
    const nested = record.error;
    if (typeof nested === "object" && nested !== null) {
      const nestedMessage = (nested as Record<string, unknown>).message;
      if (typeof nestedMessage === "string") return nestedMessage;
    }
    if (typeof record.message === "string") return record.message;
    if (typeof record.error === "string") return record.error;
  }
  if (typeof error === "string") return error;
  return "Erro desconhecido";
}

export interface ParsedAuthError {
  code: string | null;
  message: string;
  userMessage: ErrorMessageConfig | null;
}

/**
 * Parse Better Auth error object
 * Handles both error objects and response.error objects
 */
export function parseAuthError(error: unknown): ParsedAuthError {
  // Handle null/undefined
  if (!error) {
    return {
      code: null,
      message: "Erro desconhecido",
      userMessage: null,
    };
  }

  // Handle Error objects
  if (error instanceof Error) {
    const code = readErrorCode(error);
    const message = readErrorMessage(error);

    // Try to get user-friendly message from error code
    let userMessage: ErrorMessageConfig | null = null;
    if (code) {
      userMessage = getErrorMessage(code);
    }

    // If no user message found, try to infer from message
    if (!userMessage) {
      const inferredCode = inferErrorCodeFromMessage(message);
      if (inferredCode) {
        userMessage = getErrorMessage(inferredCode);
      }
    }

    return {
      code,
      message,
      userMessage,
    };
  }

  // Handle plain objects with error structure
  if (typeof error === "object") {
    const code = readErrorCode(error);
    const message = readErrorMessage(error);

    // Try to get user-friendly message from error code
    let userMessage: ErrorMessageConfig | null = null;
    if (code) {
      userMessage = getErrorMessage(code);
    }

    // If no user message found, try to infer from message
    if (!userMessage) {
      const inferredCode = inferErrorCodeFromMessage(String(message));
      if (inferredCode) {
        userMessage = getErrorMessage(inferredCode);
      }
    }

    return {
      code,
      message: String(message),
      userMessage,
    };
  }

  // Handle string errors
  if (typeof error === "string") {
    const inferredCode = inferErrorCodeFromMessage(error);
    const userMessage = inferredCode ? getErrorMessage(inferredCode) : null;

    return {
      code: inferredCode,
      message: error,
      userMessage,
    };
  }

  // Fallback
  return {
    code: null,
    message: "Erro desconhecido",
    userMessage: null,
  };
}

/**
 * Get user-friendly error message for display
 * Returns the user message if available, otherwise falls back to the error message
 */
export function getUserFriendlyErrorMessage(error: unknown): {
  title: string;
  description?: string;
} {
  const parsed = parseAuthError(error);

  if (parsed.userMessage) {
    return {
      title: parsed.userMessage.title,
      description: parsed.userMessage.description,
    };
  }

  // Fallback to error message
  return {
    title: "Erro ao processar solicitação",
    description: parsed.message,
  };
}

